import { FormBuilder } from '@/components/formBuilder';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { KText } from '@/ui-library/components';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { Box, Button, Center, Flex, useDisclosure } from '@chakra-ui/react';
import { CheckCircle, Download01, File04, XClose } from '@kula-ai/icons-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CenterSpinner } from '@/components/ats';
import {
  extractTokenArrays,
  getFilledValues,
  getUpdatedTokens,
} from '@/components/ats/OfferInitiation/offerFormHelpers';
import { useReplacementUploader } from '@/components/ats/OfferInitiation/PreviewOffer/useReplacementUploader';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  offerDetailsAPI,
  useGetAllOfferTemplatesQuery,
  useUploadReplacementMutation,
  useRemoveOfferDocumentMutation,
} from '../../api';
import { TokenValidation } from './TokenValidation';
import { DocumentPreview } from './DocumentPreview';
import { EmptyTokensForm } from './EmptyTokensForm';
import { EmptyTokensWarning } from './EmptyTokensWarning';
import { DocumentTabs } from './DocumentTabs';
import { AddMoreFilesButton } from './AddMoreFilesButton';
import type { TemplatesFormProps } from './TemplatesForm.interface';
import { useOfferStore } from '../../store';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useKFlags } from '@/hooks/use-k-flags';
import { useShowToast } from '@/hooks';
import { ModalWithForm } from '@/components/ats';

const STATUS_CONFIG = {
  unverified: 'This template has unverified tokens',
  error: 'This template has invalid tokens',
  verified: 'All tokens verified',
  no_tokens: 'No tokens found',
};

export const NewTemplatesForm = ({
  templatesFormRef,
  initialValues,
  detailsFormRef,
  tabIndex,
  currentOffer,
  detailsFormConfig,
  templateProps,
  setTemplateProps,
  onTemplateSelect,
  initialDepartmentId,
  initialOfficeId,
  sendMode = false,
  existingFormValues = {},
}: TemplatesFormProps) => {
  const [regenerateKey, setRegenerateKey] = useState(0);
  const [isTemplateSelectionComplete, setIsTemplateSelectionComplete] =
    useState(true);
  const [sendModeTokensFilled, setSendModeTokensFilled] = useState(false);
  // Whether the user has clicked "Edit offer" in the warning card to reveal the inline form.
  const [showEditForm, setShowEditForm] = useState(false);
  const [pendingReplaceFile, setPendingReplaceFile] = useState<File | null>(
    null
  );
  const esignReplaceDisclosure = useDisclosure();
  const setHasValidTokens = useOfferStore((state) => state.setHasValidTokens);
  const hasInitializedRef = useRef(false);
  const offerPayload = useOfferStore((state) => state.offerPayload || null);
  const { currentJob } = useSelector(getAtsJobCreate);
  const jobEmploymentType = currentJob?.data?.basic_info?.employment_type;
  const flags = useKFlags();
  const multiDocsEnabled = !!flags.tOfferTemplateMultiDocuments;
  const toast = useShowToast();

  const filterParams = useMemo(
    () => {
      if (tabIndex === 0) return null;
      return {
        filter_by: {
          department_ids:
            detailsFormRef?.current?.values?.ats_department_id ??
            initialDepartmentId,
          office_ids:
            detailsFormRef?.current?.values?.ats_office_id ?? initialOfficeId,
          ...(jobEmploymentType && { employment_type: [jobEmploymentType] }),
        },
        items: 100,
      };
    },
    [tabIndex, jobEmploymentType, initialDepartmentId, initialOfficeId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { data: templatesData, isFetching } = useGetAllOfferTemplatesQuery(
    filterParams ?? {},
    { enabled: filterParams !== null }
  );
  const templates = useMemo(() => templatesData?.data || [], [templatesData]);
  const currentTemplate = templateProps?.template_value;

  // Multi-doc: the BE returns offer_documents only when its flag is on, so the
  // array's presence is the source of truth. Tabs switch which doc's tokens show.
  const docs = useMemo(
    () => currentTemplate?.offer_documents || [],
    [currentTemplate?.offer_documents]
  );
  const isMultiDoc = multiDocsEnabled && docs.length > 0;
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const selectedDoc = docs[selectedDocIndex];
  const setIsMultiDocTemplate = useOfferStore(
    (state) => state.setIsMultiDocTemplate
  );
  // Files added via "Add more files" — rendered in the doc rail (not the bottom bar) for multi-doc.
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const removeOfferAttachment = useOfferStore(
    (state) => state.removeOfferAttachment
  );

  const handleRemoveAttachment = async (attachmentId: number) => {
    await offerDetailsAPI.deleteAttachment({
      offerId: currentOffer?.id,
      attachmentId,
    });
    removeOfferAttachment(attachmentId);
  };

  // The offer may sit on a per-offer copy (kind "single"); match it back to a library template by
  // its document set so the dropdown stays meaningful, otherwise surface it as "Custom".
  const dropdownTemplateValue = useMemo(() => {
    if (!currentTemplate?.id) return undefined;
    if (currentTemplate.kind === 'inline') return currentTemplate.id;
    // Per-offer (kind "single") matching only applies to multi-doc. Single-doc / flag-off
    // keeps the original behavior (no forced value, dropdown hidden for a kind "single" copy).
    if (!isMultiDoc) return undefined;
    const currentNames = (currentTemplate.offer_documents || []).map(
      (d) => d.filename
    );
    const match = templates.find((t) => {
      const names = (t.offer_documents || []).map((d) => d.filename);
      return (
        names.length === currentNames.length &&
        names.every((name, i) => name === currentNames[i])
      );
    });
    return match?.id ?? 'custom';
  }, [currentTemplate, templates, isMultiDoc]);

  const isCustomTemplate = dropdownTemplateValue === 'custom';

  // Keep the select showing the matched template (or "Custom") even though the offer is on a
  // per-offer copy whose id isn't in the options list.
  useEffect(() => {
    if (dropdownTemplateValue !== undefined) {
      templatesFormRef?.current?.setFieldValue?.(
        'ats_offer_template_id',
        dropdownTemplateValue
      );
    }
  }, [dropdownTemplateValue, templatesFormRef]);

  useEffect(() => {
    setSelectedDocIndex(0);
  }, [currentTemplate?.id]);

  // Tell the footer whether the panel already shows "Add more files" (multi-doc),
  // so it can hide the footer copy; reset on unmount so the footer button returns.
  useEffect(() => {
    setIsMultiDocTemplate(isMultiDoc);
    return () => setIsMultiDocTemplate(false);
  }, [isMultiDoc, setIsMultiDocTemplate]);

  // Extract template ID from initialValues to avoid re-renders from object reference changes
  const initialTemplateId = useMemo(
    () => initialValues?.templates?.ats_offer_template_id,
    [initialValues?.templates?.ats_offer_template_id]
  );
  const initialTemplateValue = useMemo(
    () => initialValues?.templates?.ats_offer_template,
    [initialValues?.templates?.ats_offer_template]
  );

  // Every per-offer template mutation (replace/remove) returns the updated template; treat it as
  // the new source of truth — swap it in, clamp the selected tab, and re-trigger preview.
  const applyTemplateMutation = async (data) => {
    if (!data?.id) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: data.id,
      template_value: data,
    }));
    const lastIndex = Math.max((data.offer_documents?.length || 1) - 1, 0);
    setSelectedDocIndex((current) => Math.min(current, lastIndex));
    setRegenerateKey((prev) => prev + 1);
    setIsTemplateSelectionComplete(false);
    try {
      if (onTemplateSelect) {
        await onTemplateSelect(data.id, data);
      }
      setIsTemplateSelectionComplete(true);
    } catch (error) {
      setIsTemplateSelectionComplete(true);
      throw error;
    }
  };

  const uploadReplacement = useUploadReplacementMutation({
    onSuccess: applyTemplateMutation,
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to replace document',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const removeOfferDocument = useRemoveOfferDocumentMutation({
    onSuccess: applyTemplateMutation,
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to remove document',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Replace the currently-selected document. Sends documentIndex whenever the offer has
  // offer_documents (BE flag on), regardless of the FE flag — the BE requires it.
  const replaceSelectedDocument = (file) => {
    if (!file) return;
    uploadReplacement.mutate({
      id: currentOffer?.id,
      file,
      ...(docs.length > 0 ? { documentIndex: selectedDocIndex } : {}),
    });
  };

  // Fallback text-button uploader (used only when the preview — and its built-in Replace — is hidden).
  const handleFileChange = (event) =>
    replaceSelectedDocument(event.target.files?.[0]);

  // PDF viewer "Replace" — mirrors ReplacementUploader: gate on esign confirmation when needed.
  const handleReplaceUpload = (files: File[]) => {
    const file = files?.[0];
    if (!file) return;
    if ((currentOffer as any)?.esign_envelope?.id) {
      setPendingReplaceFile(file);
      esignReplaceDisclosure.onOpen();
    } else {
      replaceSelectedDocument(file);
    }
  };

  const confirmEsignReplace = () => {
    esignReplaceDisclosure.onClose();
    if (pendingReplaceFile) replaceSelectedDocument(pendingReplaceFile);
    setPendingReplaceFile(null);
  };

  const handleRemoveDocument = (index) => {
    removeOfferDocument.mutate({ id: currentOffer?.id, documentIndex: index });
  };

  const { ReplacementUploader } = useReplacementUploader({
    handleFileChange,
    replacedData: uploadReplacement.data,
    isReplacing: uploadReplacement.isPending,
    atsOffer: currentOffer,
  });

  // Set initial template from initialValues (only once)
  useEffect(() => {
    if (!initialTemplateId || hasInitializedRef.current) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: initialTemplateId,
      template_value: initialTemplateValue,
    }));
    hasInitializedRef.current = true;
  }, [initialTemplateId, initialTemplateValue, setTemplateProps]);

  // Calculate token validation
  const tokenValidation = useMemo(() => {
    if (!currentTemplate?.id || !currentTemplate?.tokens) {
      setHasValidTokens(true);
      return {
        validTokens: [],
        invalidTokens: [],
        filledTokensNotInTemplate: [],
        nonFilledValidTokens: [],
        hasValidationIssues: false,
      };
    }

    // User already filled and saved the empty-token form — treat as valid so
    // the footer reappears and the PDF preview is shown without re-triggering
    // the warning card.
    if (sendModeTokensFilled) {
      setHasValidTokens(true);
      return {
        validTokens: [],
        invalidTokens: [],
        filledTokensNotInTemplate: [],
        nonFilledValidTokens: [],
        hasValidationIssues: false,
      };
    }

    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
    });

    const tokenArrays = extractTokenArrays({
      templateTokens: getUpdatedTokens(currentTemplate?.tokens),
      filledValues,
    });

    const hasInvalidTokens =
      tokenArrays.invalidTokens?.length > 0 ||
      tokenArrays.nonFilledValidTokens?.length > 0;

    setHasValidTokens(!hasInvalidTokens);

    return {
      ...tokenArrays,
      hasValidationIssues: hasInvalidTokens,
    };
  }, [
    currentTemplate,
    detailsFormConfig,
    detailsFormRef,
    setHasValidTokens,
    offerPayload,
    sendModeTokensFilled,
  ]);

  // Memoize separately so switching tabs (selectedDoc change) doesn't also rerun
  // when form values (offerPayload/detailsFormConfig) change.
  const selectedDocTokens = useMemo(
    () => getUpdatedTokens(selectedDoc?.tokens || {}),
    [selectedDoc]
  );

  // Per-selected-doc token sections for display. The aggregate `tokenValidation`
  // above stays the source of truth for generation + the Next gate (worst-of all docs).
  const displayValidation = useMemo(() => {
    if (!isMultiDoc) return tokenValidation;
    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
    });
    return extractTokenArrays({
      templateTokens: selectedDocTokens,
      filledValues,
    });
  }, [
    isMultiDoc,
    selectedDocTokens,
    tokenValidation,
    detailsFormConfig,
    detailsFormRef,
    offerPayload,
  ]);

  // Generation/preview gate for the selected tab: multi-doc uses the selected doc's
  // own validity so a verified doc previews even when another doc is invalid.
  const selectedDocHasIssues = isMultiDoc
    ? (displayValidation.invalidTokens?.length || 0) > 0 ||
      (displayValidation.nonFilledValidTokens?.length || 0) > 0
    : tokenValidation.hasValidationIssues;

  // Form configuration
  const formConfig = useMemo(
    () => [
      {
        type: 'kSelect',
        name: 'ats_offer_template_id',
        label: 'Select offer letter template',
        placeholder: 'Select template',
        isRequired: true,
        options: [
          // Display-only entry for an offer customised away from any library template.
          ...(isCustomTemplate
            ? [
                {
                  label: 'Custom',
                  value: 'custom',
                  desc: 'Customised for this offer',
                  isDisabled: true,
                },
              ]
            : []),
          ...[...templates]
            .sort((a, _b) => (a?.status === 'verified' ? -1 : 1))
            .map((temp) => ({
              label: temp?.name,
              value: temp?.id,
              desc:
                Object.keys(temp?.tokens ?? {})?.length === 0
                  ? STATUS_CONFIG['no_tokens']
                  : STATUS_CONFIG[temp?.status],
              // isDisabled: temp?.status !== 'verified',
            })),
        ],
        isLoading: isFetching,
        descKey: 'desc',
        components: {
          Option: SelectWithDescriptionOption,
        },
        menuPortalTarget: document.body,
        customStyles: {
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        },
        customOnChange: async (value) => {
          if (!value?.value || value.value === 'custom') return;
          const selectedTemplate = templates?.find(
            (i) => i?.id === value?.value
          );
          setTemplateProps((prev) => ({
            ...prev,
            selected_template_id: value?.value,
            template_value: selectedTemplate,
          }));
          setRegenerateKey((prev) => prev + 1);
          setIsTemplateSelectionComplete(false);
          setShowEditForm(false);

          // Call the API to update the offer with the selected template
          try {
            if (onTemplateSelect && value?.value && selectedTemplate) {
              await onTemplateSelect(value?.value, selectedTemplate);
            }
            setIsTemplateSelectionComplete(true);
          } catch (error) {
            setIsTemplateSelectionComplete(true);
            throw error;
          }
        },
      },
    ],
    [
      templates,
      isFetching,
      setTemplateProps,
      onTemplateSelect,
      isCustomTemplate,
    ]
  );

  const handleRegenerate = () => {
    setRegenerateKey((prev) => prev + 1);
  };

  if (uploadReplacement.isPending) {
    return <CenterSpinner h="400px" />;
  }

  return (
    <Box h="100%" w="100%">
      {/* Selection dropdown: shown for a library template, no template, or a multi-doc per-offer
          copy (so it still surfaces the source name or "Custom"). Hidden for a single-doc per-offer
          copy — same as before this feature. */}
      {(currentTemplate?.kind === 'inline' ||
        !currentTemplate?.id ||
        isMultiDoc) && (
        <Box mt="-12px">
          <FormBuilder
            initialValues={initialValues?.templates}
            config={formConfig}
            formRef={templatesFormRef}
          />
        </Box>
      )}

      {/* Template Content */}
      {currentTemplate?.id && (
        <Flex
          h="100%"
          w="100%"
          gap={isMultiDoc ? '16px' : '0'}
          {...(isMultiDoc && {
            borderTop: '1px solid',
            borderRight: '1px solid',
            borderColor: 'border-color',
            pt: '10px',
          })}
        >
          {isMultiDoc && (
            <DocumentTabs
              docs={docs}
              selectedIndex={selectedDocIndex}
              onSelect={setSelectedDocIndex}
              onRemoveDocument={handleRemoveDocument}
              attachments={offerAttachments}
              onRemoveAttachment={handleRemoveAttachment}
              footer={<AddMoreFilesButton offerId={currentOffer?.id} />}
            />
          )}
          <Box flex="1" minW="0" h="100%" w="100%">
            {/* Single Template Upload Display. kind "single" means per-offer (not one-document),
                so a per-offer multi-doc copy must keep the rail and skip this card. */}
            {currentTemplate?.kind === 'single' && !isMultiDoc && (
              <Flex
                p="12px"
                border="1px solid"
                borderColor="border-color"
                borderRadius="sm"
                gap="12px"
                justifyContent="space-between"
                alignItems="center"
                mb="12px"
              >
                <Flex gap="12px" flex="1" alignItems="center" overflow="hidden">
                  <Center
                    minH="44px"
                    minW="44px"
                    bgColor="secondary-50"
                    borderRadius="sm"
                  >
                    <File04 size={20} />
                  </Center>
                  <TruncatedTextWithTooltip
                    {...{ text: currentTemplate?.offer_document?.filename }}
                  />
                </Flex>
                <Flex gap="12px" alignItems="center">
                  <Center
                    p="6px 12px"
                    bgColor="pastel-2-light"
                    borderRadius="sm"
                    alignItems="center"
                    gap="6px"
                  >
                    <CheckCircle size={16} color="success-300" />
                    <KText color="success-300">Uploaded</KText>
                  </Center>
                  <Box
                    as="button"
                    type="button"
                    cursor="pointer"
                    display="flex"
                    alignItems="center"
                    onClick={() => {
                      setTemplateProps((prev) => ({
                        ...prev,
                        selected_template_id: null,
                        template_value: {},
                      }));
                    }}
                  >
                    <XClose size={16} />
                  </Box>
                </Flex>
              </Flex>
            )}

            {/* Send mode with validation issues: warning card → inline form on "Edit offer" */}
            {sendMode &&
            tokenValidation.hasValidationIssues &&
            !sendModeTokensFilled ? (
              showEditForm ? (
                <EmptyTokensForm
                  nonFilledValidTokens={tokenValidation.nonFilledValidTokens}
                  detailsFormConfig={detailsFormConfig}
                  currentOffer={currentOffer}
                  applicationId={currentOffer?.application_id}
                  existingFormValues={existingFormValues}
                  onCancel={() => {
                    setShowEditForm(false);
                    setTemplateProps((prev) => ({
                      ...prev,
                      selected_template_id: null,
                      template_value: {},
                    }));
                  }}
                  onSaveSuccess={() => {
                    setSendModeTokensFilled(true);
                    setHasValidTokens(true);
                    handleRegenerate();
                  }}
                />
              ) : (
                <EmptyTokensWarning
                  nonFilledValidTokens={tokenValidation.nonFilledValidTokens}
                  invalidTokens={tokenValidation.invalidTokens}
                  detailsFormConfig={detailsFormConfig}
                  onGoToDetails={() => setShowEditForm(true)}
                  onDownload={() =>
                    downloadFileFromUrl({
                      url: currentTemplate?.offer_document?.url,
                      filename: currentTemplate?.offer_document?.filename,
                      useDirect: true,
                    })
                  }
                  ReplacementUploader={ReplacementUploader}
                  isUploadLoading={uploadReplacement.isPending}
                />
              )
            ) : (
              <>
                {/* Actions Row */}
                <Flex
                  gap="12px"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <TokenValidation
                    filledTokensNotInTemplate={
                      tokenValidation.filledTokensNotInTemplate
                    }
                  />
                  {!multiDocsEnabled && (
                    <Flex gap="12px">
                      <Button
                        leftIcon={<Download01 size={16} />}
                        cursor="pointer"
                        onClick={() =>
                          downloadFileFromUrl({
                            url: isMultiDoc
                              ? selectedDoc?.url
                              : currentTemplate?.offer_document?.url,
                            filename: isMultiDoc
                              ? selectedDoc?.filename
                              : currentTemplate?.offer_document?.filename,
                            useDirect: true,
                          })
                        }
                        variant="quaternary"
                      >
                        Download
                      </Button>
                      <ReplacementUploader
                        isLoading={uploadReplacement.isPending}
                      />
                    </Flex>
                  )}
                </Flex>

                {/* Token Validation Display (per selected document) */}
                {(displayValidation?.nonFilledValidTokens?.length > 0 ||
                  displayValidation?.invalidTokens?.length > 0) && (
                  <TokenValidation
                    validTokens={displayValidation.validTokens}
                    invalidTokens={displayValidation.invalidTokens}
                    nonFilledValidTokens={
                      displayValidation.nonFilledValidTokens
                    }
                  />
                )}

                {/* Document Preview */}
                <DocumentPreview
                  offerId={currentOffer?.id}
                  templateId={templateProps?.selected_template_id}
                  hasValidationIssues={
                    sendMode
                      ? !sendModeTokensFilled &&
                        tokenValidation.hasValidationIssues
                      : selectedDocHasIssues
                  }
                  onRegenerateTrigger={handleRegenerate}
                  regenerateKey={regenerateKey}
                  isTemplateSelectionComplete={isTemplateSelectionComplete}
                  isMultiDoc={isMultiDoc}
                  selectedDocIndex={selectedDocIndex}
                  onReplace={handleReplaceUpload}
                  showReplace={multiDocsEnabled}
                  fileName={
                    isMultiDoc
                      ? selectedDoc?.filename
                      : currentTemplate?.offer_document?.filename
                  }
                />
              </>
            )}
          </Box>
        </Flex>
      )}
      {esignReplaceDisclosure.isOpen &&
        (() => {
          const esignEnvelope = (currentOffer as any)?.esign_envelope;
          const nonVoidable = [
            'rejected_by_candidate',
            'rejected_by_company_signer',
            'accepted_by_candidate',
          ].includes(currentOffer?.offer_status ?? '');
          const providerMap: Record<string, { name: string; item: string }> = {
            docusign: { name: 'Docusign', item: 'envelope' },
            pandadocs: { name: 'PandaDoc', item: 'document' },
            adobe: { name: 'Adobe Sign', item: 'agreement' },
          };
          const provider = providerMap[esignEnvelope?.kind] ?? null;
          const headerText = provider
            ? nonVoidable
              ? `Upload replacement: Unable to void the ${provider.name} ${provider.item}`
              : `Note: This action will void the ${provider.name} ${provider.item}`
            : 'Upload replacement';
          const subHeaderText = provider
            ? nonVoidable
              ? undefined
              : `Uploading a replacement will void the existing ${provider.name} ${provider.item}. You can create a new ${provider.item} for this candidate after uploading.`
            : undefined;
          return (
            <ModalWithForm
              withForm={false}
              isOpen={esignReplaceDisclosure.isOpen}
              onClose={esignReplaceDisclosure.onClose}
              headerText={headerText}
              subHeaderText={subHeaderText}
              primaryCtaText="Continue to upload replacement"
              handleSubmit={confirmEsignReplace}
            />
          );
        })()}
    </Box>
  );
};
